/**
 * Qualification Revoke Confirm Dialog (Epic C-2 — Kwalificaties, Story C-2.2, UX-C3)
 *
 * AD-15 dryRun-uitzet-bevestiging: toont zodra het uitzetten van een
 * kwalificatie op toekomstige indelingen leunt. Zelfde custom-modal-overlay
 * + Escape-to-close-patroon als `TaakArchiveerDialog.tsx` — geen shadcn/ui.
 */
import { useEffect } from 'react';

interface QualificationRevokeConfirmDialogProps {
  employeeFirstName: string;
  upcomingAssignmentCount: number;
  onCancel: () => void;
  onConfirm: () => void;
  isPending?: boolean;
}

export function QualificationRevokeConfirmDialog({
  employeeFirstName,
  upcomingAssignmentCount,
  onCancel,
  onConfirm,
  isPending = false,
}: QualificationRevokeConfirmDialogProps) {
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onCancel();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [onCancel, isPending]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="kwalificatie-uitzetten-overlay">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="kwalificatie-uitzetten-dialog"
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 className="mb-4 text-lg font-bold text-[#1E293B]">Kwalificatie uitzetten</h2>
        <p className="text-sm text-gray-700" data-testid="kwalificatie-uitzetten-copy">
          {employeeFirstName} heeft {upcomingAssignmentCount} toekomstige indelingen die deze kwalificatie vereisen. Die worden
          gemarkeerd als &apos;kwalificatie ontbreekt&apos; (waarschuwing, geen blokkade).
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            data-testid="kwalificatie-uitzetten-cancel"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-md border border-[#E2E8F0] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            data-testid="kwalificatie-uitzetten-confirm"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-md bg-[#2F5A7A] px-4 py-2 text-sm font-medium text-white hover:bg-[#2F5A7A]/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? 'Bezig...' : 'Toch uitzetten'}
          </button>
        </div>
      </div>
    </div>
  );
}
